<template>
  <v-row no-gutters align="center">
    <v-col cols="12" v-if="isFetched">
      <v-row no-gutters align="center">
        <v-col cols="auto" class="pr-2 text-left font-weight-medium">
          {{ $i18n.t(translationPath) }}
        </v-col>
        <v-col cols="auto" class="text-left">
          <base-rounded-button
            icon="mdi-plus"
            size="x-small"
            color="success"
            :tooltip="$i18n.t('admin.parcelsUldk.addMapping')"
            @click="dialogAction('add')"
          />
        </v-col>
      </v-row>
      <v-row no-gutters class="pt-7">
        <v-col cols="12">
          <v-data-table
            :headers="tableHeaders"
            :items="tableItems"
            disable-pagination
            hide-default-footer
            style="border: 1px solid rgba(0, 0, 0, 0.12)"
          >
            <template #item.options="{item}">
              <dots-menu
                :items="[{ name: 'edit' }, { name: 'delete' }]"
                @delete="dialogAction('delete', item)"
                @edit="dialogAction('edit', item)"
              />
            </template>
          </v-data-table>
        </v-col>
      </v-row>
    </v-col>
    <main-loader v-else />
    <main-dialog
      v-if="isMainDialogLoaded"
      :is-visible.sync="isMainDialogVisible"
      v-bind="mainDialogBind"
      @agree="dialogActionHandler"
    />
    <main-input-dialog
      v-if="isMainInputDialogLoaded"
      :is-visible.sync="isMainInputDialogVisible"
      v-bind="mainInputDialogBind"
      @agree="dialogActionHandler"
    >
      <template #datasource.input="data">
        <v-col cols="6">
          <data-input
            v-bind="data"
            v-model="currentDataSource"
            @input="setCurrentDataSourceAttributes({ value: $event })"
          />
        </v-col>
      </template>
    </main-input-dialog>
  </v-row>
</template>
<script>
import { call, get } from 'vuex-pathify';

import DataInput from '@/components/DataInput';
import DotsMenu from '@/components/DotsMenu';
import MainLoader from '@/components/MainLoader';

export default {
  name: 'ParcelsSettingsUldkAttributes',
  components: {
    DataInput,
    DotsMenu,
    MainDialog: () => import('@/components/MainDialog'),
    MainInputDialog: () => import('@/components/MainInputDialog'),
    MainLoader,
  },
  props: {
    translationPath: {
      type: String,
    },
  },
  computed: {
    uldkAttributesMappings: get('search/uldkAttributesMappings'),
    dataSources: get('layers/metadata'),
    dataSourcesNamesArray() {
      return this.$_objectToArray(this.dataSources || [])
        .filter(dataSource => dataSource.geometry_type?.includes('polygon'))
        .map(dataSource => {
          return { value: dataSource.name, text: dataSource.verbose_name };
        });
    },
    dialogsActionsData() {
      return {
        add: {
          agreePath: 'dialog.agreeAdd',
          component: 'MainInputDialog',
        },
        delete: {
          agreePath: 'dialog.agreeDelete',
          component: 'MainDialog',
        },
        edit: {
          agreePath: 'dialog.agreeSave',
          component: 'MainInputDialog',
          initAction: this.setCurrentDataSource,
        },
      };
    },

    currentDataSourceTextAttributes() {
      return this.currentDataSourceAttributes?.filter(attribute => attribute.dataType?.name === 'text') || [];
    },
    currentDataSourceNumberAttributes() {
      return (
        this.currentDataSourceAttributes?.filter(attribute =>
          ['integer', 'decimal', 'float'].includes(attribute.dataType?.name)
        ) || []
      );
    },
    capitalizeCurrentAction() {
      return this.$_capitalize(this.currentAction);
    },
    currentDialogActionData() {
      return this.dialogsActionsData[this.currentAction] || {};
    },
    mainDialogBind() {
      return {
        loading: this.isMainDialogLoading,
        ...(this.isMainDialogVisible
          ? {
              title: this.$i18n.t(`admin.parcelsUldk.actions.${this.currentAction}MappingTitle`),
              body: this.$i18n.t(`admin.parcelsUldk.actions.confirmMapping${this.capitalizeCurrentAction}`, {
                name: this.currentActionData.verboseValues.datasource,
              }),
              agreeText: this.currentDialogActionData.agreePath,
            }
          : {}),
      };
    },
    mainInputDialogBind() {
      return {
        loading: this.isMainInputDialogLoading,
        dialogWidth: '30%',
        ...(this.isMainInputDialogVisible
          ? {
              titleTranslation: this.$i18n.t(`admin.parcelsUldk.actions.${this.currentAction}MappingTitle`),
              agreeText: this.currentDialogActionData.agreePath,
              inputs: this.mainInputDialogInputs,
              customIsValid: this.currentDialogActionData.customIsValid === false ? false : true,
            }
          : {}),
      };
    },
    mainInputDialogInputs() {
      const elementEditInputs = [
        {
          name: 'datasource',
          translationPath: 'admin.parcelsUldk.attributes.datasource',
          cols: '12',
          rules: [
            v => !!v || v == '0' || 'rules.required',
            v =>
              !this.$_objectToArray(this.uldkAttributesMappings).find(
                item => this.currentActionData?.id !== item.id && item.datasource === v
              ) || 'rules.dataSourceIsTaken',
          ],
          dataType: { name: 'select' },
          items: this.dataSourcesNamesArray.filter(dataSource => {
            return (
              this.currentActionData?.datasource === dataSource.value ||
              !this.$_objectToArray(this.uldkAttributesMappings).find(item => item.datasource === dataSource.value)
            );
          }),
        },
        ...this.uldkAttributesText.map(attribute => {
          return {
            name: attribute,
            translationPath: `admin.parcelsUldk.attributes.${attribute}`,
            cols: '12',
            dataType: { name: 'select' },
            items: this.currentDataSourceTextAttributes,
            disabled: !this.currentDataSourceAttributes,
          };
        }),
        ...this.uldkAttributesNumber.map(attribute => {
          return {
            name: attribute,
            translationPath: `admin.parcelsUldk.attributes.${attribute}`,
            cols: '12',
            dataType: { name: 'select' },
            items: this.currentDataSourceNumberAttributes,
            disabled: !this.currentDataSourceAttributes,
          };
        }),
      ];
      const mainInputDialogInputsDict = {
        add: elementEditInputs,
        edit: elementEditInputs,
      };
      return (mainInputDialogInputsDict[this.currentAction] || []).map(input => {
        return {
          ...input,
          value: JSON.parse(JSON.stringify(this.currentActionData?.[input.name] || null)),
        };
      });
    },

    tableHeaders() {
      return ['datasource', ...this.uldkAttributesText, ...this.uldkAttributesNumber, 'options'].map(attribute => {
        return {
          text: attribute === 'options' ? '' : this.$i18n.t(`admin.parcelsUldk.attributes.${attribute}`),
          value: attribute === 'options' ? attribute : `verboseValues.${attribute}`,
          sortable: attribute !== 'options',
          align: 'center',
          width: attribute === 'options' ? '60px' : '200px',
        };
      });
    },
    tableItems() {
      return Object.values(this.uldkAttributesMappings)
        .map(mapping => {
          const { datasource, id } = mapping;
          const dS = this.dataSources[datasource];
          if (!dS) return;
          const rMapping = { id, datasource: dS.verbose_name };
          [...this.uldkAttributesText, ...this.uldkAttributesNumber].forEach(attribute => {
            rMapping[attribute] = dS.attributes_schema.attributes.find(
              attr => attr.name === mapping[attribute]
            )?.verbose_name;
          });
          return { ...mapping, verboseValues: rMapping };
        })
        .filter(dS => !!dS);
    },
  },
  data: () => ({
    currentAction: '',
    currentActionData: undefined,
    currentDataSource: '',
    currentDataSourceAttributes: undefined,

    isFetched: false,
    isMainDialogLoaded: false,
    isMainDialogLoading: false,
    isMainDialogVisible: false,
    isMainInputDialogLoaded: false,
    isMainInputDialogLoading: false,
    isMainInputDialogVisible: false,

    uldkAttributesText: ['voivodeship', 'county', 'community', 'district', 'sheet', 'plot', 'teryt'],
    uldkAttributesNumber: ['area'],
  }),
  methods: {
    addUldkAttributesMapping: call('search/addUldkAttributesMapping'),
    deleteUldkAttributesMapping: call('search/deleteUldkAttributesMapping'),
    editUldkAttributesMapping: call('search/editUldkAttributesMapping'),
    getUldkAttributesMappings: call('search/getUldkAttributesMappings'),
    async dialogAction(type, value) {
      this.currentAction = type;
      this.currentActionData = value;
      const { component, initAction } = this.dialogsActionsData[type];
      if (initAction) initAction({ type, value });
      this[`is${component}Loaded`] = true;
      this[`is${component}Loading`] = false;
      this[`is${component}Visible`] = true;
    },
    async dialogActionHandler(data) {
      const dialogComponent = this.currentDialogActionData?.component;
      this[`is${dialogComponent}Loading`] = true;
      try {
        await this[`handler${this.capitalizeCurrentAction}Mapping`](data);
        this[`is${dialogComponent}Loading`] = false;
      } catch {
        this[`is${dialogComponent}Loading`] = false;
      }
    },
    async handlerDeleteMapping() {
      await this.deleteUldkAttributesMapping(this.currentActionData.id);
      this[`is${this.currentDialogActionData?.component}Visible`] = false;
    },
    async handlerAddMapping() {
      const payload = this.$_arrayToObject(this.mainInputDialogInputs, 'name', 'value');
      payload['datasource'] = this.currentDataSource;
      await this.addUldkAttributesMapping(payload);
      this[`is${this.currentDialogActionData?.component}Visible`] = false;
    },
    async handlerEditMapping() {
      const payload = this.$_arrayToObject(this.mainInputDialogInputs, 'name', 'value');
      await this.editUldkAttributesMapping({
        id: this.currentActionData.id,
        payload,
      });
      this[`is${this.currentDialogActionData?.component}Visible`] = false;
    },
    async init() {
      await this.getUldkAttributesMappings();
      this.isFetched = true;
    },

    setCurrentDataSource({ value } = {}) {
      this.currentDataSource = value.datasource;
      this.setCurrentDataSourceAttributes({ value: value.datasource });
    },
    setCurrentDataSourceAttributes({ value } = {}) {
      this.currentDataSourceAttributes = (this.dataSources[value]?.attributes_schema.attributes || [])
        .filter(attribute => !attribute.read_only)
        .map(attribute => {
          return { value: attribute.name, text: attribute.verbose_name, dataType: attribute.data_type };
        });
    },
  },
  mounted() {
    this.init();
    this.$watch(
      vm => [vm.isMainDialogVisible, vm.isMainInputDialogVisible],
      nV => {
        const [isMainDialogVisible, isMainInputDialogVisible] = nV;
        if (!isMainDialogVisible && !isMainInputDialogVisible) {
          this.currentAction = '';
          this.currentActionData = undefined;
          this.currentDataSource = '';
          this.currentDataSourceAttributes = undefined;
          this.isMainDialogLoading = false;
          this.isMainInputDialogLoading = false;
        }
      }
    );
  },
};
</script>
<style lang="scss" scoped>
::v-deep {
  table > tbody > tr > td:last-child,
  table > thead > tr > th:last-child {
    background-color: red;
    position: sticky !important;
    position: -webkit-sticky !important;
    right: 0;
    z-index: 10;
    background-color: white;
    opacity: 1;
  }
  table > thead > tr > th:last-child {
    z-index: 10;
    border-left: 1px solid rgba(0, 0, 0, 0.12);
  }
  table > tbody > tr > td:last-child {
    border-left: 1px solid rgba(0, 0, 0, 0.12);
  }
  .theme--light.v-data-table > .v-data-table__wrapper > table > thead > tr:last-child > th {
    white-space: nowrap;
  }
}
</style>
